import { create } from 'zustand';
import { message } from 'antd';
import axiosInstance from '../utils/axiosInstance';
import { getCachedData, setCachedData, clearCacheByKey } from '../utils/cacheUtil';

const useCustomerStore = create((set) => ({
  customers: [],
  loading: false,
  error: null,

  fetchCustomers: async () => {
    const cached = getCachedData('customers');
    if (cached) {
      set({ customers: cached, loading: false, error: null });
      return;
    }

    set({ loading: true });
    try {
      const response = await axiosInstance.get('/customers/');
      setCachedData('customers', response.data);
      set({ 
        customers: response.data,
        loading: false,
        error: null 
      });
    } catch (error) {
      set({ error: error.message, loading: false });
      message.error('Failed to fetch customers');
    }
  },

  createCustomer: async (customerData) => {
    set({ loading: true });
    try {
      const response = await axiosInstance.post('/customers/', customerData);
      clearCacheByKey('customers');
      set(state => ({
        customers: [...state.customers, response.data],
        loading: false,
      }));
      message.success('Customer created successfully');
      return { success: true, data: response.data };
    } catch (error) {
      set({ loading: false });
      // Don't show message here since we'll display it in the form
      // message.error(errorMessage);
      return { success: false, error };
    }
  },

  updateCustomer: async (id, customerData) => {
    set({ loading: true });
    try {
      const response = await axiosInstance.put(`/customers/${id}/`, customerData);
      clearCacheByKey('customers');
      set(state => ({
        customers: state.customers.map(customer => 
          customer.id === id ? response.data : customer
        ),
        loading: false,
      }));
      message.success('Customer updated successfully');
      return { success: true, data: response.data };
    } catch (error) {
      set({ loading: false });
      // Don't show message here since we'll display it in the form
      // message.error(errorMessage);
      return { success: false, error };
    }
  },

  deleteCustomer: async (id) => {
    try {
      await axiosInstance.delete(`/customers/${id}/`);
      clearCacheByKey('customers');
      set(state => ({
        customers: state.customers.filter(customer => customer.id !== id),
      }));
      message.success('Customer deleted successfully');
      return { success: true };
    } catch (error) {
      message.error('Failed to delete customer');
      return { success: false, error };
    }
  },

  // Helper method to get a single customer by ID
  getCustomerById: (id) => {
    const state = useCustomerStore.getState();
    return state.customers.find(customer => customer.id === id);
  },

  // Search customers by name or email
  searchCustomers: async (query) => {
    set({ loading: true });
    try {
      const response = await axiosInstance.get(`/customers/?search=${query}`);
      set({ 
        customers: response.data,
        loading: false,
        error: null 
      });
    } catch (error) {
      set({ error: error.message, loading: false });
      message.error('Failed to search customers');
    }
  },
}));

export default useCustomerStore;
